import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  LinearProgress,
  Tooltip,
  IconButton,
  Collapse,
  Alert,
  CircularProgress,
  Divider,
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Psychology as PsychologyIcon,
  BarChart as BarChartIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import {
  subscribeToPatternLibrary,
  getPatternsForCaseStudy,
  KNOWN_PATTERN_TYPES,
} from '../../../services/patternLibraryService';

/** Confidence level to color/label mapping */
const CONFIDENCE_CONFIG = {
  high: { color: 'success', label: 'High Confidence', description: 'Identified in many investigations' },
  medium: { color: 'warning', label: 'Medium Confidence', description: 'Appears in several investigations' },
  low: { color: 'info', label: 'Low Confidence', description: 'Emerging pattern, limited data' },
  emerging: { color: 'default', label: 'Emerging', description: 'Newly identified, needs more data' },
};

/** OE element labels */
const ELEMENT_LABELS = {
  driftPerception: 'Drift in Perception',
  driftWork: 'Drift in Work',
  traps: 'Traps',
  roadblocks: 'Roadblocks',
  landmines: 'Landmines',
};

/**
 * Single pattern strength card.
 */
function PatternCard({ pattern }) {
  const [expanded, setExpanded] = useState(false);
  const confCfg = CONFIDENCE_CONFIG[pattern.confidenceLevel] || CONFIDENCE_CONFIG.emerging;
  const confidencePercent = Math.round((pattern.confidence || 0) * 100);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2,
        mb: 1.5,
        borderLeft: 4,
        borderLeftColor: `${confCfg.color}.main`,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography variant="subtitle2" fontWeight="bold">
              {pattern.label}
            </Typography>
            <Chip
              label={confCfg.label}
              size="small"
              color={confCfg.color}
              variant="outlined"
              sx={{ height: 22, fontSize: '0.7rem' }}
            />
          </Box>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {pattern.description}
          </Typography>

          {/* Frequency indicator */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Tooltip title={`This pattern has been identified in ${pattern.frequency} CSB investigation${pattern.frequency !== 1 ? 's' : ''}`}>
              <Chip
                icon={<BarChartIcon sx={{ fontSize: 16 }} />}
                label={`${pattern.frequency} investigation${pattern.frequency !== 1 ? 's' : ''}`}
                size="small"
                variant="outlined"
                sx={{ height: 24 }}
              />
            </Tooltip>

            <Tooltip title={`Confidence: ${confidencePercent}% — ${confCfg.description}`}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 120 }}>
                <LinearProgress
                  variant="determinate"
                  value={confidencePercent}
                  color={confCfg.color}
                  sx={{ flex: 1, height: 6, borderRadius: 3 }}
                />
                <Typography variant="caption" fontWeight="bold" color="text.secondary">
                  {confidencePercent}%
                </Typography>
              </Box>
            </Tooltip>

            {pattern.element && (
              <Chip
                label={ELEMENT_LABELS[pattern.element] || pattern.element}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: '0.7rem' }}
              />
            )}

            {pattern.adjustedThreshold < pattern.baseThreshold && (
              <Tooltip title={`Detection threshold lowered from ${pattern.baseThreshold} to ${pattern.adjustedThreshold} due to pattern frequency`}>
                <Chip
                  icon={<TrendingUpIcon sx={{ fontSize: 14 }} />}
                  label={`Threshold: ${pattern.baseThreshold} → ${pattern.adjustedThreshold}`}
                  size="small"
                  color="warning"
                  variant="outlined"
                  sx={{ height: 22, fontSize: '0.7rem' }}
                />
              </Tooltip>
            )}
          </Box>
        </Box>

        <IconButton size="small" onClick={() => setExpanded(!expanded)}>
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      {/* Expanded: show case studies containing this pattern */}
      <Collapse in={expanded}>
        <Divider sx={{ my: 1.5 }} />
        <Typography variant="caption" fontWeight="bold" color="text.secondary" gutterBottom>
          Identified in these investigations:
        </Typography>
        <Box sx={{ mt: 0.5 }}>
          {(pattern.caseStudyTitles || []).map((title, idx) => (
            <Typography key={idx} variant="body2" sx={{ py: 0.25, pl: 1, borderLeft: '2px solid', borderColor: 'divider' }}>
              {title}
            </Typography>
          ))}
        </Box>
        {pattern.industries && pattern.industries.length > 0 && (
          <Box sx={{ mt: 1, display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
            <Typography variant="caption" color="text.secondary">Industries:</Typography>
            {pattern.industries.map((ind) => (
              <Chip key={ind} label={ind.replace('_', ' & ')} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
            ))}
          </Box>
        )}
      </Collapse>
    </Paper>
  );
}

/**
 * PatternStrength — shows pattern frequency and confidence for a specific case study.
 *
 * Displays which precursor patterns from this case study appear across other investigations,
 * with confidence scores that increase as more case studies contain the same pattern.
 *
 * @param {Object} props
 * @param {string} props.caseStudyId - The case study ID to show patterns for
 * @param {boolean} [props.showAll=false] - If true, show all patterns (not just this case study's)
 */
export default function PatternStrength({ caseStudyId, showAll = false }) {
  const [allPatterns, setAllPatterns] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = subscribeToPatternLibrary((patterns) => {
      setAllPatterns(patterns);
      setLoading(false);
    });
    return unsub;
  }, []);

  const relevantPatterns = showAll
    ? allPatterns
    : getPatternsForCaseStudy(caseStudyId, allPatterns);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (relevantPatterns.length === 0) {
    return (
      <Alert severity="info" icon={<PsychologyIcon />} sx={{ mb: 2 }}>
        No cross-report pattern data available yet. Pattern analysis runs automatically as CSB reports are ingested.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PsychologyIcon color="primary" />
        <Typography variant="h6" fontWeight="bold">
          {showAll ? 'Cross-Report Pattern Library' : 'Pattern Strength Analysis'}
        </Typography>
      </Box>

      {!showAll && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          These precursor patterns from this case study have been cross-referenced against all
          other investigations. Higher confidence means the pattern appears consistently across
          multiple incidents, making it a stronger predictor of organizational risk.
        </Typography>
      )}

      {showAll && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Pattern library built from cross-referencing all ingested CSB investigation reports.
          Patterns that appear in more investigations receive higher confidence scores and
          lower detection thresholds for earlier warning.
        </Typography>
      )}

      {/* Summary stats */}
      <Box sx={{ display: 'flex', gap: 2, mb: 2, flexWrap: 'wrap' }}>
        <Chip
          icon={<BarChartIcon />}
          label={`${relevantPatterns.length} pattern${relevantPatterns.length !== 1 ? 's' : ''} identified`}
          color="primary"
          variant="outlined"
        />
        {relevantPatterns.filter((p) => p.confidenceLevel === 'high').length > 0 && (
          <Chip
            icon={<WarningIcon />}
            label={`${relevantPatterns.filter((p) => p.confidenceLevel === 'high').length} high-confidence`}
            color="success"
            variant="outlined"
          />
        )}
        {relevantPatterns.some((p) => p.adjustedThreshold < p.baseThreshold) && (
          <Chip
            icon={<TrendingUpIcon />}
            label="Thresholds adjusted"
            color="warning"
            variant="outlined"
          />
        )}
      </Box>

      {/* Pattern cards */}
      {relevantPatterns.map((pattern) => (
        <PatternCard key={pattern.patternType} pattern={pattern} />
      ))}
    </Box>
  );
}
